import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MessageCircle, Wrench, Sparkles, ArrowDown } from "lucide-react";
import { IMAGES, WHATSAPP_URL } from "@/lib/site";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.25 + i * 0.12, ease: [0.16, 1, 0.3, 1] },
  }),
};

const MaskLine = ({ children, i, className = "" }) => (
  <span className="block overflow-hidden">
    <motion.span variants={line} custom={i} initial="hidden" animate="show" className={`block ${className}`}>
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section ref={ref} id="top" className="relative min-h-[100svh] overflow-hidden" data-testid="hero-section">
      {/* Parallax background */}
      <motion.div style={{ y, scale }} className="absolute inset-0 z-0">
        <img src={IMAGES.hero} alt="Charlotte home interior" className="h-full w-full object-cover" />
      </motion.div>
      {/* Split-house diagonal overlay */}
      <div className="absolute inset-0 z-[1] bg-petrol/80" />
      <div
        className="absolute inset-0 z-[1] bg-graphite/85"
        style={{ clipPath: "polygon(58% 0, 100% 0, 100% 100%, 42% 100%)" }}
      />
      <div className="absolute inset-0 z-[2] bg-gradient-to-t from-petrol via-petrol/20 to-transparent" />

      <div className="relative z-10 max-w-7xl mx-auto px-5 lg:px-12 pt-36 pb-20 min-h-[100svh] flex flex-col justify-center">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.6 }}
          className="flex items-center gap-3 mb-7"
        >
          <span className="inline-flex items-center gap-2 rounded-full border border-offwhite/25 bg-offwhite/10 backdrop-blur px-4 py-1.5 text-xs font-semibold tracking-[0.15em] uppercase text-offwhite">
            <span className="h-1.5 w-1.5 rounded-full bg-mustard animate-pulse" />
            Family-Run · Charlotte, NC
          </span>
        </motion.div>

        <h1 className="font-display font-extrabold text-offwhite tracking-tight leading-[0.98] text-[13vw] sm:text-6xl md:text-7xl lg:text-[5.5rem] max-w-5xl">
          <MaskLine i={0}>From Repairs</MaskLine>
          <MaskLine i={1}>
            to <span className="text-mustard">Clean.</span>
          </MaskLine>
          <MaskLine i={2} className="text-offwhite/90 text-[7vw] sm:text-4xl md:text-5xl lg:text-[3.4rem] mt-2 md:mt-4">
            We've got your home covered.
          </MaskLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.7 }}
          className="mt-8 max-w-xl text-base md:text-lg font-light text-offwhite/80 leading-relaxed"
        >
          Two trusted service lines under one family name — expert handyman repairs and
          spotless residential & commercial cleaning across Charlotte and the surrounding area.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.7 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-cta-button"
            className="group inline-flex items-center gap-2.5 rounded-full bg-mustard px-8 py-4 text-base font-semibold text-ink hover:scale-[1.04] hover:shadow-[0_12px_40px_rgba(232,163,23,0.45)] transition-transform duration-300"
          >
            <MessageCircle className="h-5 w-5" strokeWidth={2.4} />
            Get a Free Quote
          </a>
          <a
            href="#services"
            data-testid="hero-services-link"
            className="inline-flex items-center gap-2 rounded-full border border-offwhite/30 px-7 py-4 text-base font-medium text-offwhite hover:bg-offwhite/10 transition-colors duration-300"
          >
            Explore Services
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 0.8 }}
          className="mt-16 flex items-center gap-8 text-offwhite/70"
        >
          <div className="flex items-center gap-2.5">
            <Wrench className="h-5 w-5 text-mustard" strokeWidth={2} />
            <span className="text-sm font-medium">Handyman & Repairs</span>
          </div>
          <div className="h-5 w-px bg-offwhite/25" />
          <div className="flex items-center gap-2.5">
            <Sparkles className="h-5 w-5 text-mustard" strokeWidth={2} />
            <span className="text-sm font-medium">Residential & Commercial Cleaning</span>
          </div>
        </motion.div>
      </div>

      <motion.a
        href="#services"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.7 }}
        className="absolute bottom-7 left-1/2 -translate-x-1/2 text-offwhite/60 hidden md:block"
        aria-label="Scroll down"
      >
        <ArrowDown className="h-5 w-5 animate-bounce" />
      </motion.a>
    </section>
  );
}
